function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useContext, useEffect, forwardRef, useImperativeHandle, useLayoutEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { HelpButton } from '../../HelpButton';
import ValidationMessage from '../../Input/components/common/ValidationMessage/ValidationMessage';
import { TAB_KEYCODE, ARROW_DOWN_KEY, ARROW_UP_KEY } from '../../Utils/constant';
import { SelectMenuContext } from '../context';
import styles from '@dtsl/css-select-menu/dist/index.module.css';

// eslint-disable-next-line react/display-name
const SelectMenu = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    children,
    className,
    inlineSelectMenuStyles,
    inputStyles = {},
    isControlButton = false,
    isControlChip = false,
    isError = false,
    disabled = false,
    specificationText = '',
    label = '',
    requiredLabel = false,
    isAbsoluteSelectMenu = false,
    identifier,
    isSuccess,
    validationText,
    accessibleErrorText,
    accessibleSuccessText,
    helpButtonMetaData
  } = _ref;
  const {
    isOpen,
    setFocused,
    searchValue,
    handleSearch,
    setIsOpen,
    isOverlayOpen,
    setIsOverlayOpen,
    isMobile,
    selectMenuControlRef,
    isControlSearchable,
    selectMenuMultiSelectRef,
    selectMenuGroupListRef,
    selectMenuBodyRef,
    selectMenuChildFolderRef,
    searchBarRef,
    setSelectMenuWidth,
    onClose,
    selectMenuRef
  } = useContext(SelectMenuContext);
  const selectMenuClass = classNames({
    [styles['sib-selectmenu']]: true,
    [styles['selectmenu-button']]: isControlButton,
    [styles['is-open']]: isMobile ? isOverlayOpen : isOpen,
    [styles['sib-selectmenu-error']]: isError,
    [styles['sib-selectmenu-absolute']]: isAbsoluteSelectMenu,
    [className]: !!className
  });
  const labelClass = classNames({
    [styles['sib-selectmenu-label']]: true,
    [className]: !!className
  });
  const specificationTextClass = classNames({
    [styles['sib-selectmenu-help']]: true,
    [className]: !!className
  });
  useImperativeHandle(ref, () => ({
    toggleMenu: setIsOpen
  }));
  const handleDocumentClick = event => {
    if (selectMenuBodyRef?.current && selectMenuControlRef?.current) {
      if (!selectMenuBodyRef.current?.contains(event.target) && !selectMenuControlRef.current?.contains(event.target)) {
        if (isMobile && isOverlayOpen) {
          setIsOverlayOpen(false);
          onClose(event, isOverlayOpen);
          searchValue && handleSearch('');
        } else if (isOpen) {
          setIsOpen(false);
          searchValue && handleSearch('');
          onClose(event, isOpen);
        }
        selectMenuControlRef.current?.classList.remove(styles['sib-selectmenu-control-focused']);
      }
    } else {
      selectMenuControlRef.current?.classList.remove(styles['sib-dropdown-control-focused']);
    }
  };
  const handleDocumentTabKeyPress = event => {
    if (selectMenuBodyRef.current) {
      if (!selectMenuBodyRef.current?.contains(event.target) && !selectMenuControlRef.current?.contains(event.target)) {
        if (isMobile && isOverlayOpen) {
          setIsOverlayOpen(false);
          onClose(event, isOverlayOpen);
          searchValue && handleSearch('');
        } else if (isOpen) {
          setIsOpen(false);
          searchValue && handleSearch('');
          onClose(event, isOpen);
        }
        selectMenuControlRef.current?.classList.remove(styles['sib-selectmenu-control-focused']);
        return;
      }
    }
    const _createBtn = selectMenuBodyRef?.current?.children[2]?.firstChild;
    if (event.keyCode === TAB_KEYCODE) {
      let mlist = [];
      let hideSelectMenu = false;
      if (selectMenuMultiSelectRef.current) {
        if (selectMenuGroupListRef.current) {
          Array.from(selectMenuMultiSelectRef.current.children).forEach(group => {
            if (group.classList.contains(styles['select-all'])) {
              if (!group.classList.contains(styles['locked'])) {
                mlist.push(group);
              }
            } else {
              Array.from(group.children[1]?.children).forEach(groupChild => {
                if (!groupChild.classList.contains(styles['locked'])) {
                  mlist.push(groupChild); //Group Options
                }
              });
            }
          });
        } else {
          Array.from(selectMenuMultiSelectRef.current.children).forEach(panelListItem => {
            if (!panelListItem.classList.contains(styles['locked'])) {
              mlist.push(panelListItem); //Single Options
            }
          });
        }
        if (!event.shiftKey && event.target.id === mlist[mlist.length - 1].querySelector(`.${styles['sib-checkbox-input']}`).id) {
          hideSelectMenu = true;
        }
      }
      if (!event.shiftKey && (event.target.classList.contains(styles['sib-selectmenu-options']) || event.target.classList.contains(styles['sib-selectmenu-option']))) {
        hideSelectMenu = true;
      }
      if (_createBtn) {
        if (event.target == _createBtn && (event.key == 'Tab' || event.keyCode === TAB_KEYCODE) && !event.shiftKey) {
          hideSelectMenu = true;
        } else {
          hideSelectMenu = false;
        }
      }
      if (hideSelectMenu) {
        if (isMobile && isOverlayOpen) {
          setIsOverlayOpen(false);
          searchValue && handleSearch('');
          onClose(event, isOverlayOpen);
        } else if (isOpen) {
          setIsOpen(false);
          searchValue && handleSearch('');
          onClose(event, isOpen);
        }
        setFocused(null);
        selectMenuControlRef.current?.classList.remove(styles['sib-selectmenu-control-focused']);
      }

      //Focus mapping on trigger in case of searchbar visible
      if (searchBarRef && searchBarRef.current) {
        if (event.shiftKey && event.target.closest(`.${styles['sib-selectmenu--searchbar']}`)) {
          selectMenuControlRef.current?.classList.add(styles['sib-selectmenu-control-focused']);
        } else if (!event.shiftKey && event.target.classList.contains(styles['sib-selectmenu-control-focused'])) {
          event.target.classList.remove(styles['sib-selectmenu-control-focused']);
        }
      }
    }
    if (event.key === ARROW_DOWN_KEY && event.target.classList.contains(styles['sib-tab--link']) || event.key === ARROW_UP_KEY && (event.target === selectMenuBodyRef?.current?.children[1]?.children[0]?.children[0] || event.target.classList.contains(styles['sib-selectmenu-option-folder-header']))) {
      if (selectMenuBodyRef && selectMenuBodyRef.current) {
        if (selectMenuChildFolderRef && selectMenuChildFolderRef.current && selectMenuChildFolderRef.current.style.display === 'block') {
          selectMenuChildFolderRef.current.focus();
        } else {
          selectMenuBodyRef.current.children[1]?.children[0]?.focus();
        }
      }
    }
  };
  useLayoutEffect(() => {
    if (!(isControlButton || isControlChip)) {
      setSelectMenuWidth(selectMenuRef?.current?.offsetWidth);
    }
  }, [selectMenuRef?.current?.offsetWidth]);
  useEffect(() => {
    document.addEventListener('click', handleDocumentClick, {
      capture: true
    });
    document.addEventListener('keydown', handleDocumentTabKeyPress);
    if (isControlSearchable) {
      if (isMobile) {
        !isOverlayOpen && searchValue && handleSearch('');
      } else {
        !isOpen && searchValue && handleSearch('');
      }
    }
    return () => {
      document.removeEventListener('click', handleDocumentClick, {
        capture: true
      });
      document.removeEventListener('keydown', handleDocumentTabKeyPress);
    };
  }, [isOpen, isOverlayOpen, disabled]);
  useEffect(() => {
    if (searchValue && searchValue.length > 0) {
      if (isMobile) {
        !isOverlayOpen && setIsOverlayOpen(true);
      } else {
        !isOpen && setIsOpen(true);
      }
    }
  }, [searchValue]);
  const helpButtonClasses = classNames({
    [styles['sib-selectmenu--helpbutton']]: true,
    [helpButtonMetaData?.className]: !!helpButtonMetaData?.className
  });
  return /*#__PURE__*/React.createElement("div", {
    ref: selectMenuRef,
    disabled: disabled,
    className: selectMenuClass,
    style: inlineSelectMenuStyles
  }, !!label && !isControlButton && !isControlChip && /*#__PURE__*/React.createElement("label", {
    className: labelClass,
    htmlFor: identifier
  }, label, requiredLabel ? /*#__PURE__*/React.createElement("span", {
    className: styles['sib-selectmenu-label__required']
  }, "*") : null, helpButtonMetaData ? /*#__PURE__*/React.createElement(HelpButton, _extends({}, helpButtonMetaData, {
    className: helpButtonClasses
  })) : null), specificationText && !isControlButton && !isControlChip ? /*#__PURE__*/React.createElement("p", {
    id: `${identifier}-specificationText`,
    className: specificationTextClass
  }, specificationText) : null, /*#__PURE__*/React.createElement("div", {
    className: styles['sib-selectmenu--root'],
    style: inputStyles
  }, children), /*#__PURE__*/React.createElement(ValidationMessage, {
    id: identifier,
    isError: isError,
    isSuccess: isSuccess,
    validationText: validationText,
    accessibleErrorText: accessibleErrorText,
    accessibleSuccessText: accessibleSuccessText
  }));
});
SelectMenu.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  isControlButton: PropTypes.bool,
  isError: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  requiredLabel: PropTypes.bool.isRequired,
  specificationText: PropTypes.string.isRequired,
  isAbsoluteSelectMenu: PropTypes.bool,
  inlineSelectMenuStyles: PropTypes.object,
  inputStyles: PropTypes.object,
  autoPlacement: PropTypes.bool,
  isControlChip: PropTypes.bool,
  identifier: PropTypes.string,
  accessibleErrorText: PropTypes.string,
  accessibleSuccessText: PropTypes.string,
  isSuccess: PropTypes.bool,
  validationText: PropTypes.string,
  helpButtonMetaData: PropTypes.object
};
export default SelectMenu;